/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { CustomerStorageService } from 'projects/content-service-cms/src/app/customer/service/customer-storage.service';
import { PRODUCT_TYPE } from 'projects/content-service-cms/src/app/data';
import {
  ICentralAssetPlatformService,
  IRequestService,
  PagingRequestDTO,
} from 'projects/content-service-cms/src/app/data/api';
import {
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  from,
  take,
  takeUntil,
} from 'rxjs';
import { CollectionScreenStore } from '../../collection-screen.state';
import { CommonModule } from '@angular/common';
import { ReferenceEntryComponent } from './components/reference-entry/reference-entry.component';
import {
  ConfigurationDTO,
  ConfigurationResultDTO,
} from '@reactivereality/cs-api-sdk';

import {
  CollectionFilters,
  CollectionSearchFilterBarComponent,
} from './components/collection-search-filter-bar/collection-search-filter-bar.component';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { IPaginationDataProviderInterface } from '../../../request-overview/services/pagination-data-provider.interface';
import { ComponentPaginationDataProvider } from '../../../request-overview/services/component-pagination-data-provider';
import { RequestListPaginationComponent } from '../../../request-overview/components/request-list-page/request-list-pagination/request-list-pagination.component';
import { SelectionModalStore } from '../../../request-overview/components/create-request-modal/store/selection-modal-store';
import { SelectedFilters } from 'projects/web-ui-component-library/src';
import {
  CollectionAvatarReference,
  CollectionGarmentReference,
  CollectionSceneReference,
} from '../../../collection-overview/collection.interface';

export interface IReferenceSelectionData {
  id: string;
  type: PRODUCT_TYPE;
  name: string;
  reference: string;
  organizationId: string;
  thumbnail?: string;
  additionalDataUrl?: string;
  results: ConfigurationResultDTO[];
  synced: boolean;
  collectionEnabled?: boolean;
}

@Component({
  selector: 'app-reference-selection',
  standalone: true,
  imports: [
    CommonModule,
    ReferenceEntryComponent,
    CollectionSearchFilterBarComponent,
    PaginationModule,
    RequestListPaginationComponent,
  ],
  templateUrl: './reference-selection.component.html',
  styleUrl: './reference-selection.component.scss',
  providers: [
    SelectionModalStore,
    {
      provide: IPaginationDataProviderInterface,
      useClass: ComponentPaginationDataProvider,
    },
  ],
})
export class ReferenceSelectionComponent implements AfterViewInit, OnDestroy {
  public requestList: IReferenceSelectionData[] = [];
  private _abortController: AbortController = new AbortController();
  private _currentPage = 1;
  private _onDestroy$: Subject<void>;
  private _defaultPageNumber = 10;
  public entryCount = 0;
  public totalCount = -1;
  public selectedType: PRODUCT_TYPE;
  public garments: IReferenceSelectionData[] | undefined = undefined;
  public hideAlreadyAdded = false;
  public searchText: string = '';
  constructor(
    public createCollectionStore: CollectionScreenStore,
    public store: SelectionModalStore,
    private _customerStorageService: CustomerStorageService,
    private _requestService: IRequestService,
    private _cap: ICentralAssetPlatformService,
  ) {}
  async ngAfterViewInit(): Promise<void> {
    this._onDestroy$ = new Subject<void>();

    combineLatest([
      this.createCollectionStore.searchProductType$,
      this.createCollectionStore.filters$,
      this.store.currentPage$,
      this.store.garmentsLoaded$,
      this.store.advancedGarmentSearch$,
    ])
      .pipe(debounceTime(500))
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(
        async ([
          type,
          filters,
          currentPage,
          garmentsLoaded,
          advancedGarmentSearch,
        ]) => {
          if (this.selectedType !== type) {
            this.selectedType = type;
            this._currentPage = 1;
            this.store.updateCurrentPage(1);
          } else {
            this._currentPage = currentPage;
          }

          if (type === PRODUCT_TYPE.GARMENT_2D && advancedGarmentSearch) {
            if (garmentsLoaded) {
              this.createCollectionStore.setFilteredCollections(
                this.store.allGarments$,
              );
              this.store.updatePageCount(1);
              return;
            }
            this.store.updateGarmentsLoading(true);
            let page = 1;
            let continueFetching = true;
            let allConfigurations = [];
            const totalPages = await this.getTotalPages(
              [type],
              this.formatFilters(filters),
              this.formatTags(filters),
            );

            const parallelFetchedPages = Math.min(
              this._defaultPageNumber,
              totalPages,
            );
            const start = Date.now();
            while (continueFetching) {
              // Determine the range of pages to fetch in this batch
              const pageNumbers = Array.from(
                { length: parallelFetchedPages },
                (_, i) => i + page,
              );

              // Fetch pages in parallel
              const response = await Promise.all(
                pageNumbers.map((p) =>
                  this.getLazyRequests(
                    [type],
                    this.formatFilters(filters),
                    this.formatTags(filters),
                    p,
                  ),
                ),
              );
              this.totalCount = response.map((r) => r?.total)[0];
              const configurationsResults = response.map(
                (r) => r?.configurations,
              );

              // Flatten the results and filter out any empty responses
              const configurations = configurationsResults
                ?.filter((c) => c?.length > 0)
                .flat();

              if (configurations === undefined || configurations.length === 0) {
                continueFetching = false;
              } else {
                // Process each batch of configurations
                for (const configurations of configurationsResults) {
                  if (
                    configurations === undefined ||
                    configurations.length === 0
                  ) {
                    continue;
                  }
                  let mapped = configurations.map((c) => ({
                    id: c.id,
                    name: c.name,
                    reference: `gid://pictofit/sku/${c.sku}`,
                    type: c.productTypeId,
                    thumbnail: c.previewUrl,
                    organizationId: c.customerId,
                    additionalDataUrl: undefined,
                    results: c.results,
                  }));

                  mapped = await this.getAdditionalData(mapped);

                  allConfigurations = [...allConfigurations, ...mapped];
                  this.entryCount = allConfigurations.length;
                }
                this.garments = allConfigurations;
                this.createCollectionStore.setFilteredCollections(
                  this.garments,
                );
                page += parallelFetchedPages; // Increment by the number of pages fetched in parallel
                //if we have less then to this._defaultPageNumber pages in total we can break here.
                if (parallelFetchedPages < this._defaultPageNumber) {
                  break;
                }
              }
            }
            console.warn(`Fetched ${page} pages in ${Date.now() - start}ms`);
            this.store.updateGarmentsLoading(false);
            this.store.updateGarmentsLoaded(true);
            // After all batches are processed, update the garments and other states
            this.garments = allConfigurations;
            this.createCollectionStore.setFilteredCollections(this.garments);
            this.store.updateAllGarments(this.garments);
            this.store.updatePageSize(1);
            this.applyAdvancedFilter();
          } else if (type !== PRODUCT_TYPE.UNDEFINED) {
            this.getRequests(
              type === PRODUCT_TYPE.GARMENT_2D
                ? [PRODUCT_TYPE.GARMENT_2D, PRODUCT_TYPE.ACCESSORY_3D]
                : [type],
              this.formatFilters(filters),
              this.formatTags(filters),
            ).subscribe((response) => {
              let mapped = response.configurations.map((c) => {
                return {
                  id: c.id,
                  name: c.name,
                  reference: `gid://pictofit/sku/${c.sku}`,
                  type: c.productTypeId,
                  thumbnail: c.previewUrl,
                  organizationId: c.customerId,
                  additionalDataUrl:
                    type === PRODUCT_TYPE.OUTFIT_2D_PARALLAX
                      ? getOutfitParallaxInfoUrl(c)
                      : undefined,
                  results: c.results,
                } as IReferenceSelectionData;
              });
              //check if synced to CAP
              const mappedPromise = mapped.map(async (r) => {
                const id = await this._cap.getId(r.organizationId, r.reference);
                return {
                  ...r,
                  synced: id?.length > 0,
                };
              });
              Promise.all(mappedPromise).then((res) => {
                mapped = res;
                //filter out not-collection-enabled outfits
                if (type === PRODUCT_TYPE.OUTFIT_2D_PARALLAX) {
                  mapped = mapped.filter((request) => {
                    const webResults = request.results.filter(
                      (res) => res.format === 'web',
                    );
                    if (webResults.length > 0) {
                      request.collectionEnabled = webResults[0].files.some(
                        (file) => {
                          //if we find this file, it should be an outfit made to be added to collections
                          return file.name.includes('large_0.65.webp');
                        },
                      );
                    }
                    return webResults.length > 0;
                  });
                }
                this.createCollectionStore.setFilteredCollections(mapped);
                this.store.updatePageCount(response.paging.pageCount);
              });
            });
          }
        },
      );

    this.createCollectionStore.filteredRequests$.subscribe((data) => {
      this.requestList = data;
    });

    this.createCollectionStore.garments$.subscribe((_) => {
      this.applyAdvancedFilter();
    });
  }

  updateAdvancedGarmentSearch($event: Event) {
    const input = $event.target as HTMLInputElement;
    this.store.updateAdvancedGarmentSearch(input.checked);
    this.store.updateCurrentPage(1);
  }

  onHideAdded($event: Event) {
    const input = $event.target as HTMLInputElement;
    this.hideAlreadyAdded = input?.checked;
    this.applyAdvancedFilter();
  }

  onSearchText($event: Event) {
    this.searchText = ($event.target as HTMLInputElement).value;
    this.applyAdvancedFilter();
  }

  addFilteredGarments() {
    this.store.allGarments$.pipe(take(1)).subscribe((garments) => {
      for (const garment of garments) {
        if (garment.type === PRODUCT_TYPE.GARMENT_2D && garment.synced) {
          this.createCollectionStore.addOrUpdateGarment({
            id: garment.id,
            thumbnail: garment.thumbnail,
            name: garment.name,
            reference: garment.reference,
            organizationId: garment.organizationId,
          });
        }
      }
    });
  }

  applyAdvancedFilter() {
    let filteredGarments = this.garments;
    if (this.hideAlreadyAdded) {
      this.createCollectionStore.garments$
        .pipe(take(1))
        .subscribe((addedGarments) => {
          filteredGarments = filteredGarments.filter(
            (g) =>
              !addedGarments.some((addedGarment) => addedGarment.id === g.id),
          );
        });
    }
    if (this.searchText !== '') {
      filteredGarments = filteredGarments.filter((garment) => {
        return (
          garment.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
          garment.reference
            .toLowerCase()
            .includes(this.searchText.toLowerCase())
        );
      });
    }
    this.store.updateAllGarments(filteredGarments);
  }
  formatFilters(filters: SelectedFilters<CollectionFilters>): any {
    const result = {};
    if (filters.name) {
      result['name'] = filters.name;
    }
    if (filters.sku) {
      result['sku'] = filters.sku;
    }
    if (filters.createdAt) {
      const res = [
        filters.createdAt.from.getTime() / 1000,
        filters.createdAt.to.getTime() / 1000,
      ];
      result['createdAt'] = res;
    }
    return result;
  }

  reloadGarments() {
    this.entryCount = 0;
    this.totalCount = -1;
    this.store.updateGarmentsLoaded(false);
    this.store.updateCurrentPage(-1);
  }

  formatTags(filters: SelectedFilters<CollectionFilters>): any {
    let result = '{';
    if (filters.tags) {
      filters.tags.forEach((tag) => {
        if (tag.uuid) {
          result += `"${tag.uuid}":["${tag.value}"],`;
        }
      });
    }
    if (result.endsWith(',')) {
      result = result.slice(0, -1);
    }
    result += '}';
    return result;
  }
  ngOnDestroy(): void {
    if (this._onDestroy$) {
      this._onDestroy$.next();
      this._onDestroy$.complete();
      this._onDestroy$.unsubscribe();
      this._onDestroy$ = undefined;
    }
    this.abort();
  }

  private getAdditionalData = async (
    elements:
      | CollectionAvatarReference[]
      | CollectionSceneReference[]
      | CollectionGarmentReference[],
  ) => {
    const elementsByOrg = elements.reduce((map, element) => {
      const { organizationId } = element;
      map.set(organizationId, [...(map.get(organizationId) || []), element]);
      return map;
    }, new Map<string, CollectionAvatarReference[] | CollectionSceneReference[] | CollectionGarmentReference[]>());
    const allRequests = [];
    for (const [organizationId, elementsInOrg] of elementsByOrg) {
      const refs = elementsInOrg.map((g) => g.reference);
      const additionalData = await this._cap.getAdditionalData(
        organizationId,
        refs,
        this._abortController.signal,
      );
      const requests$ = elementsInOrg.map((element) => {
        const index = additionalData.findIndex((data) =>
          data.externalReferences
            .map((e) => e.reference)
            .includes(element.reference),
        );
        if (index === -1) {
          return element;
        }
        return {
          ...element,
          thumbnail:
            additionalData[index] != undefined
              ? additionalData[index].thumbnail
              : element.thumbnail,
          synced: additionalData[index] != undefined,
        };
      });
      allRequests.push(...requests$);
    }
    return allRequests;
  };

  private getRequests(
    type: PRODUCT_TYPE[],
    filters,
    tags: { name: string; value: string }[],
  ): Observable<PagingRequestDTO> {
    let requestFilter = {
      productTypeId: type,
      stateId: [4, 13],
    };

    requestFilter = { ...requestFilter, ...filters };
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();
    return from(
      this._requestService.getAllCustomerRequests(
        this._customerStorageService.getSelectedCustomer(),
        JSON.stringify(requestFilter),
        this._currentPage,
        10,
        tags.length > 0 ? tags.toString() : undefined,
        false,
        this._abortController.signal,
      ),
    );
  }

  public abort() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = new AbortController();
      this.store.updateGarmentsLoaded(true);
      this.store.updateCurrentPage(-1);
      this.store.updateGarmentsLoading(false);
    }
  }

  private async getTotalPages(
    type: PRODUCT_TYPE[],
    filters,
    tags: { name: string; value: string }[],
  ) {
    let requestFilter = {
      productTypeId: type,
      stateId: [4, 13],
    };

    requestFilter = { ...requestFilter, ...filters };
    const response = await this._requestService.getAllCustomerRequests(
      this._customerStorageService.getSelectedCustomer(),
      JSON.stringify(requestFilter),
      1,
      1,
      tags.length > 0 ? tags.toString() : undefined,
      false,
      this._abortController.signal,
    );
    return Math.floor(response.paging.recordCount / 100) + 1;
  }

  private async getLazyRequests(
    type: PRODUCT_TYPE[],
    // eslint-disable-next-line max-lines
    filters,
    tags: { name: string; value: string }[],
    page: number,
  ) {
    let requestFilter = {
      productTypeId: type,
      stateId: [4, 13],
    };

    requestFilter = { ...requestFilter, ...filters };

    const response = await this._requestService.getAllCustomerRequests(
      this._customerStorageService.getSelectedCustomer(),
      JSON.stringify(requestFilter),
      page,
      100,
      tags.length > 0 ? tags.toString() : undefined,
      false,
      this._abortController.signal,
    );

    if (response && response.configurations?.length > 0) {
      return {
        configurations: response.configurations,
        total: response.paging?.recordCount || 0,
      };
    } else {
      return undefined;
    }
  }
}
export function getOutfitParallaxInfoUrl(c: ConfigurationDTO): string {
  //extract the json file from the config.
  const webResult = c.results.filter(
    (o) => o.format.toLocaleLowerCase() === 'web',
  );
  if (webResult.length > 0) {
    const result = webResult[0];
    const json = result.files.find((o) => o.name === 'resolved.json');
    if (json) {
      return json.url;
    } else return undefined;
  }
}
